import {
  afterNextRender,
  computed,
  DestroyRef,
  inject,
  Injector,
  signal,
  Service,
} from '@angular/core';
import {
  findScrollableAncestor,
  prefersReducedMotion,
  scrollElementIntoView,
} from '../utils/scroll-into-view.utils';

export const KEYBOARD_INSET_PROPERTY = '--keyboard-inset-height';
export const VIEWPORT_OFFSET_TOP_PROPERTY = '--visual-viewport-offset-top';
export const VIEWPORT_INSET_BOTTOM_PROPERTY = '--visual-viewport-inset-bottom';

const MIN_KEYBOARD_HEIGHT_PX = 120;

@Service()
export class KeyboardInsetService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly rootElement = document.documentElement;
  private readonly insetPxSignal = signal(0);
  private readonly offsetTopPxSignal = signal(0);
  private readonly bandBottomPxSignal = signal(0);
  private frameHandle: number | null = null;
  private measurePending = false;

  readonly insetPx = this.insetPxSignal.asReadonly();
  readonly offsetTopPx = this.offsetTopPxSignal.asReadonly();
  readonly bandBottomPx = this.bandBottomPxSignal.asReadonly();
  readonly isKeyboardOpen = computed(() => this.insetPxSignal() > 0);

  constructor() {
    const viewport =
      typeof window === 'undefined' ? undefined : window.visualViewport;
    if (!viewport) {
      return;
    }

    const schedule = () => {
      if (this.measurePending) {
        return;
      }
      // Gated on the flag, not the handle because the handle is assigned only after `requestAnimationFrame`
      // returns, which is after the callback ran wherever frames fire synchronously.
      this.measurePending = true;
      this.frameHandle = requestAnimationFrame(() => {
        this.measurePending = false;
        this.frameHandle = null;
        this.apply(
          this.measureKeyboard(viewport),
          Math.max(0, Math.round(viewport.offsetTop)),
          this.measureBandBottom(viewport)
        );
      });
    };
    const reveal = () => this.scheduleReveal();

    this.apply(
      this.measureKeyboard(viewport),
      Math.max(0, Math.round(viewport.offsetTop)),
      this.measureBandBottom(viewport)
    );
    viewport.addEventListener('resize', schedule);
    viewport.addEventListener('scroll', schedule);
    document.addEventListener('focusin', reveal);

    this.destroyRef.onDestroy(() => {
      if (this.frameHandle !== null) {
        cancelAnimationFrame(this.frameHandle);
        this.frameHandle = null;
      }
      this.measurePending = false;
      viewport.removeEventListener('resize', schedule);
      viewport.removeEventListener('scroll', schedule);
      document.removeEventListener('focusin', reveal);
      this.publishIfChanged(0, 0, 0);
    });
  }

  /** Must not subtract `offsetTop` because that reads a raised keyboard as absent. */
  private measureKeyboard(viewport: VisualViewport): number {
    const covered = this.rootElement.clientHeight - viewport.height;
    return covered >= MIN_KEYBOARD_HEIGHT_PX ? Math.round(covered) : 0;
  }

  private measureBandBottom(viewport: VisualViewport): number {
    const below =
      this.rootElement.clientHeight - (viewport.height + viewport.offsetTop);
    return Math.max(0, Math.round(below));
  }

  private apply(
    insetPx: number,
    offsetTopPx: number,
    bandBottomPx: number
  ): void {
    if (this.publishIfChanged(insetPx, offsetTopPx, bandBottomPx)) {
      this.scheduleReveal();
    }
  }

  private publishIfChanged(
    insetPx: number,
    offsetTopPx: number,
    bandBottomPx: number
  ): boolean {
    if (
      insetPx === this.insetPxSignal() &&
      offsetTopPx === this.offsetTopPxSignal() &&
      bandBottomPx === this.bandBottomPxSignal()
    ) {
      return false;
    }

    this.insetPxSignal.set(insetPx);
    this.offsetTopPxSignal.set(offsetTopPx);
    this.bandBottomPxSignal.set(bandBottomPx);
    this.rootElement.style.setProperty(KEYBOARD_INSET_PROPERTY, `${insetPx}px`);
    this.rootElement.style.setProperty(
      VIEWPORT_OFFSET_TOP_PROPERTY,
      `${offsetTopPx}px`
    );
    this.rootElement.style.setProperty(
      VIEWPORT_INSET_BOTTOM_PROPERTY,
      `${bandBottomPx}px`
    );
    return true;
  }

  private scheduleReveal(): void {
    afterNextRender(() => this.revealFocusedElement(), {
      injector: this.injector,
    });
  }

  // A control with no scrolling ancestor is left alone because the native fallback would scroll the page
  // out from under the fullscreen dropdown picker.
  private revealFocusedElement(): void {
    const focused = document.activeElement;
    if (this.insetPxSignal() === 0 || !(focused instanceof HTMLElement)) {
      return;
    }
    if (!findScrollableAncestor(focused)) {
      return;
    }

    scrollElementIntoView(focused, {
      block: 'end',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }
}
